// A treemap: one total per category, drawn as tiles whose AREA is the value.
// It is the mark for six to fifteen categories of one measure (revenue by
// channel, registrants by city) that is not another row of bars: the large
// categories read first because they are large, and the tail stays visible
// without a bar apiece. Props follow RankedBars, so a page swaps the mark
// with one word.
//
// The tiles are laid out here (a squarified layout) and drawn as plain SVG,
// through this chart module like every other chart, with no library behind
// it: recharts' treemap does not hand over the tile's text, and the text is
// the hard part. A tile label is the content, so it is never clipped and
// never crosses its tile's edge. A tile large enough prints its name, on as
// many lines as its height holds, and its value; a tile that is not prints
// only its rank number, and its full name and value sit in the numbered key
// under the plot. The decision is made
// per tile from an upper bound of the drawn text width against the tile's
// own box, so a name is never truncated.
//
// One series, one hue. Every tile is the series colour mixed into the
// surface, strongest for the largest value, and separated from its
// neighbours by a gap of surface. The strongest fill is still light enough
// for ink (measured in Chrome: ink on it is 9.2 to 1, muted ink 4.9 to 1),
// so the text colour never has to switch from tile to tile.
import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactElement } from "react";
import { ChartFigure, defaultFormat, type ChartBaseProps, type ChartHeight, type ValueFormat } from "./frame";
import type { CategoryDatum } from "./Bars";
import { KEY_GAP, keyColumnWidth, textWidthBound, tidyLines } from "./Cartesian";
import { formatPercent } from "../format";

/** Fewer categories than this is not a treemap: two or three numbers read better as figures. */
export const TREEMAP_CATEGORIES_MIN = 3;

/**
 * The most categories the smallest card can hold. A chart in a panel on a
 * 320px phone is 254 by 240px; fifteen tiles there average about 60px a
 * side, room for each one's number and for the largest to carry their names.
 * Past fifteen most tiles are bare numbers and the key is longer than the plot.
 */
export const TREEMAP_CATEGORIES_MAX = 15;

/** A negative value has no area. treemapFits refuses any; the chart gives a negative no tile. */
export const TREEMAP_VALUE_MIN = 0;

/**
 * Whether a treemap can draw this data: three to fifteen categories, at least
 * three of them above zero, and no negative value. A page calls it before it
 * picks this mark and picks another when it is false. A zero is allowed: it
 * takes no tile and stays in the key and the table.
 */
export function treemapFits(data: CategoryDatum[]): boolean {
  if (data.length > TREEMAP_CATEGORIES_MAX) return false;
  if (data.some((d) => !Number.isFinite(d.value) || d.value < TREEMAP_VALUE_MIN)) return false;
  return data.filter((d) => d.value > 0).length >= TREEMAP_CATEGORIES_MIN;
}

/** The geometry, in CSS px, which the SVG's user units are. */
const TILE_GAP = 2;
/**
 * Around a name and its value: beside the text, and above and below it. The
 * text box of a line carries its own space (3.8px above a capital, 3.8px
 * under the baseline), so 2px more above and below sets the letters about
 * 6px inside the tile, near the 8px beside them, and leaves the tile's
 * height to the lines of the name.
 */
const TILE_PAD_X = 8;
const TILE_PAD_Y = 2;
/** Around a bare rank number, which has less tile to spare. */
const NUMBER_PAD_X = 4;
const NUMBER_PAD_Y = 2;
/** One line of --text-n1: the height of its text box, so two lines never share a pixel. */
const LINE = 17;
/** The most lines a name takes in a tile, when the tile is tall enough for them and the value. */
const NAME_LINES_MAX = 4;
/** Tiles are laid out a little wider than tall, so a name has a line to sit on. */
const TILE_ASPECT = 1.5;
/** One character of Geist Mono at --text-n1, and the key's share track (6ch), in px. */
const MONO_ADVANCE = 7.8;
const SHARE_TRACK = 47;
/** The fill of the largest and of the smallest tile, as the share of the series colour mixed into the surface. */
const FILL_STRONGEST = 44;
const FILL_WEAKEST = 12;

export interface TreemapBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * A squarified layout: `values` (positive, largest first) as boxes that tile
 * `width` by `height` exactly, each with an area in proportion to its value.
 * Rows are filled along the shorter side while that keeps the tiles closer
 * to the wanted shape; the box is stretched for the pass and squeezed back,
 * which is what makes the tiles come out wider than tall.
 */
export function treemapLayout(values: number[], width: number, height: number, aspect: number = TILE_ASPECT): TreemapBox[] {
  const total = values.reduce((sum, v) => sum + v, 0);
  if (!(total > 0) || !(width > 0) || !(height > 0)) return [];
  const tall = height * aspect;
  const areas = values.map((v) => (v / total) * width * tall);
  const boxes: TreemapBox[] = [];
  let x = 0;
  let y = 0;
  let w = width;
  let h = tall;
  let at = 0;
  while (at < areas.length) {
    const side = Math.min(w, h);
    /** The worst aspect ratio in a row of this sum between its largest and smallest area. */
    const worst = (sum: number, largest: number, smallest: number) => Math.max((side * side * largest) / (sum * sum), (sum * sum) / (side * side * smallest));
    const largest = areas[at]!;
    let sum = largest;
    let end = at + 1;
    while (end < areas.length && worst(sum + areas[end]!, largest, areas[end]!) <= worst(sum, largest, areas[end - 1]!)) {
      sum += areas[end]!;
      end += 1;
    }
    const thickness = sum / side;
    let along = 0;
    for (let i = at; i < end; i++) {
      const length = areas[i]! / thickness;
      if (w >= h) boxes.push({ x, y: y + along, width: thickness, height: length });
      else boxes.push({ x: x + along, y, width: length, height: thickness });
      along += length;
    }
    if (w >= h) {
      x += thickness;
      w -= thickness;
    } else {
      y += thickness;
      h -= thickness;
    }
    at = end;
  }
  return boxes.map((b) => ({ x: b.x, y: b.y / aspect, width: b.width, height: b.height / aspect }));
}

export interface TreemapTile extends TreemapBox {
  /** Position in the data sorted largest first, from 1. The number a tile and the key share. */
  rank: number;
  label: string;
  value: number;
  /** "name": the name and the value are printed in the tile. "number": the rank only. "bare": too small even for that. */
  print: "name" | "number" | "bare";
  /** The lines the tile prints: the name on one to four lines and then the value, or the rank. */
  lines: string[];
  /** Share of the series colour in the fill, in percent. */
  strength: number;
}

/**
 * Every category with a value above zero as a tile of a `width` by `height`
 * plot, largest first, with what it prints. A tile prints its name and value
 * when every line is certain to fit inside it with the tile padding, the name
 * broken at word boundaries onto as many lines as the tile's height holds (up
 * to four); else its rank number when that fits; else nothing. Whatever is
 * not printed as a name is listed in the key.
 */
export function treemapTiles(data: CategoryDatum[], width: number, height: number, format: ValueFormat = defaultFormat, aspect: number = TILE_ASPECT): TreemapTile[] {
  const ranked = [...data].sort((a, b) => b.value - a.value);
  const drawn = ranked.filter((d) => d.value > 0);
  const boxes = treemapLayout(drawn.map((d) => d.value), width, height, aspect);
  return boxes.map((box, i) => {
    const d = drawn[i]!;
    const rank = i + 1;
    // The gap between tiles is taken from the tile, half on each side.
    const inner = { x: box.x + TILE_GAP / 2, y: box.y + TILE_GAP / 2, width: Math.max(0, box.width - TILE_GAP), height: Math.max(0, box.height - TILE_GAP) };
    const strength = Math.round(drawn.length > 1 ? FILL_STRONGEST - ((FILL_STRONGEST - FILL_WEAKEST) * i) / (drawn.length - 1) : FILL_STRONGEST);
    const base = { ...inner, rank, label: d.label, value: d.value, strength };
    const room = inner.width - TILE_PAD_X * 2;
    const value = format(d.value);
    // As many lines as the tile is tall enough for, less the one the value takes.
    const held = Math.min(NAME_LINES_MAX, Math.floor((inner.height - TILE_PAD_Y * 2) / LINE) - 1);
    const name = held >= 1 && textWidthBound(value) <= room ? tidyLines(d.label, room, held) : null;
    if (name !== null) return { ...base, print: "name" as const, lines: [...name, value] };
    const number = String(rank);
    if (textWidthBound(number) <= inner.width - NUMBER_PAD_X * 2 && LINE <= inner.height - NUMBER_PAD_Y * 2) return { ...base, print: "number" as const, lines: [number] };
    return { ...base, print: "bare" as const, lines: [] };
  });
}

/** The fill of a tile: the one series hue mixed into the surface, a flat colour. */
function tileFill(strength: number): string {
  return `color-mix(in oklab, var(--viz-1) ${strength}%, var(--color-surface))`;
}

/** Two decimals: stable markup, and finer than a device pixel. */
function round(n: number): number {
  return Math.round(n * 100) / 100;
}

export interface TreemapChartProps extends ChartBaseProps {
  /** One non-negative value per category. Drawn largest first. */
  data: CategoryDatum[];
  /** Add each value's share of the total to the key and the table. Only for values that add up to a meaningful whole. */
  showShare?: boolean;
  height?: ChartHeight;
}

interface Size {
  width: number;
  height: number;
}

/**
 * The size of the plot box, measured: before the first paint, and again
 * whenever the box changes, so the layout is always the one for the box the
 * chart actually has. Null until measured (and where nothing can measure).
 */
function usePlotSize(): [(node: HTMLDivElement | null) => void, Size | null] {
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const [size, setSize] = useState<Size | null>(null);
  useLayoutEffect(() => {
    if (node === null) return;
    const read = () => {
      const box = node.getBoundingClientRect();
      const next = { width: Math.floor(box.width), height: Math.floor(box.height) };
      if (next.width <= 0 || next.height <= 0) return;
      setSize((prev) => (prev !== null && prev.width === next.width && prev.height === next.height ? prev : next));
    };
    read();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(read);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);
  return [setNode, size];
}

/**
 * One tile per category above zero, area in proportion to the value, with
 * the name and value inside every tile that can hold them and a numbered key
 * under the plot for the rest. The plot stretches to the height its card is
 * given and is laid out again for the box it gets.
 */
export function TreemapChart({ label, summary, valueLabel, categoryLabel, format = defaultFormat, data, showShare = false, height = "md" }: TreemapChartProps): ReactElement {
  const [stage, size] = usePlotSize();
  const ranked = [...data].sort((a, b) => b.value - a.value);
  const total = ranked.reduce((sum, d) => sum + Math.max(0, d.value), 0);
  const tiles = size === null ? [] : treemapTiles(data, size.width, size.height, format);

  // What the key lists: every category whose name is not printed on its tile.
  // Under a taller neighbour the plot takes what the key leaves, so the key's
  // length feeds back into the layout. A category that enters the key
  // therefore stays in it until the width or the data changes: the key can
  // only grow, the plot can only shrink, and the two settle.
  const signature = `${size?.width ?? 0}|${ranked.map((d) => `${d.label}:${d.value}`).join("|")}`;
  const latch = useRef<{ signature: string; listed: Set<number> }>({ signature: "", listed: new Set() });
  if (latch.current.signature !== signature) latch.current = { signature, listed: new Set() };
  const listed = latch.current.listed;
  if (size === null) ranked.forEach((_, i) => listed.add(i + 1));
  else {
    for (const tile of tiles) if (tile.print !== "name") listed.add(tile.rank);
    ranked.forEach((d, i) => {
      if (!(d.value > 0)) listed.add(i + 1);
    });
  }

  // One column of the key: index, name, value in mono figures (0.6em a
  // character), and the share track when there is one.
  const inKey = ranked.filter((_, i) => listed.has(i + 1));
  const valueTrack = Math.ceil(Math.max(0, ...inKey.map((d) => format(d.value).length)) * MONO_ADVANCE);
  const keyWidth = keyColumnWidth(inKey.map((d) => d.label), KEY_GAP + valueTrack + (showShare ? KEY_GAP + SHARE_TRACK : 0));

  const table = {
    caption: label,
    head: showShare ? [categoryLabel, valueLabel, "Persentase"] : [categoryLabel, valueLabel],
    rows: ranked.map((d) => (showShare ? [d.label, format(d.value), formatPercent(Math.max(0, d.value), total)] : [d.label, format(d.value)])),
  };
  return (
    <ChartFigure label={label} summary={summary} kind="treemap" height={height} table={table}>
      <div className="con-tree" data-state={size === null ? "pending" : "drawn"}>
        <div className="con-tree-stage" ref={stage}>
          <div className="con-chart-canvas" aria-hidden="true">
            {size !== null ? (
              <svg className="con-tree-plot" width={size.width} height={size.height} viewBox={`0 0 ${size.width} ${size.height}`} focusable="false" role="presentation">
                {tiles.map((tile) => {
                  // A tile in the key shows its number, even when a later, taller box could hold its name.
                  const named = tile.print === "name" && !listed.has(tile.rank);
                  const lines = named ? tile.lines : tile.print === "bare" ? [] : [String(tile.rank)];
                  const padX = named ? TILE_PAD_X : NUMBER_PAD_X;
                  const padY = named ? TILE_PAD_Y : NUMBER_PAD_Y;
                  return (
                    <g key={tile.rank} className="con-tree-tile" data-print={named ? "name" : lines.length > 0 ? "number" : "bare"}>
                      <title>{`${tile.label}: ${format(tile.value)}`}</title>
                      <rect x={round(tile.x)} y={round(tile.y)} width={round(tile.width)} height={round(tile.height)} rx={4} style={{ fill: tileFill(tile.strength) }} />
                      {lines.map((line, i) => (
                        <text
                          key={i}
                          className={named ? (i === lines.length - 1 ? "con-tree-value" : "con-tree-name") : "con-tree-index"}
                          x={round(tile.x + padX)}
                          y={round(tile.y + padY + i * LINE)}
                          dy="1em"
                        >
                          {line}
                        </text>
                      ))}
                    </g>
                  );
                })}
              </svg>
            ) : null}
          </div>
        </div>
        {listed.size > 0 ? (
          <ol className="con-tree-key" data-share={showShare ? "" : undefined} style={{ "--con-key-col": `${keyWidth}px` } as CSSProperties}>
            {ranked.map((d, i) =>
              listed.has(i + 1) ? (
                <li key={i} className="con-tree-key-item">
                  <span className="con-tree-key-index">{i + 1}</span>
                  <span className="con-tree-key-label">{d.label}</span>
                  <span className="con-tree-key-value">{format(d.value)}</span>
                  {showShare ? <span className="con-tree-key-share">{formatPercent(Math.max(0, d.value), total)}</span> : null}
                </li>
              ) : null,
            )}
          </ol>
        ) : null}
      </div>
    </ChartFigure>
  );
}
